/**
 * Y GO Shuttle Services France - Page Contact & Devis REFONTE
 * Objectif : Page de conversion fonctionnelle et rassurante
 * Approche : Orienter la demande - Simplifier - Rassurer
 */
import React, { useState, useEffect } from 'react';
import { 
  Mail, Phone, MapPin, Clock, Send, Loader2, CheckCircle2,
  Car, Plane, Ship, Building2, HelpCircle, User, Calendar,
  MessageSquare, Users, Shield, Zap, Globe
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Header, Footer } from '../components/layout';
import { useCMSPage } from '../hooks/useCMSPage';
import { trackContactRequest, trackFormSubmission } from '../components/GoogleTagManager';
import config from 'config';

const API_URL = config.API_URL;

export default function ContactPage() {
  const { content: cmsContent, loading: cmsLoading } = useCMSPage('contact');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    message: '',
    is_business: false,
    company_name: ''
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  // SEO
  useEffect(() => {
    const meta = cmsContent?.meta || {};
    document.title = meta.title || 'Contact & Devis | Y GO Shuttle Services';
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = meta.description || 'Contactez Y GO pour un devis personnalisé. Service VTC premium Le Havre. Réponse sous 2h. Tel: 02 78 78 04 04';
    
    return () => {
      document.title = 'Y GO Shuttle Services';
    };
  }, [cmsContent]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/contact/devis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nom: formData.name,
          email: formData.email,
          telephone: formData.phone,
          type_demande: formData.service_type || 'autre',
          message: formData.message + (formData.is_business && formData.company_name ? `\n\nEntreprise: ${formData.company_name}` : '')
        })
      });

      if (response.ok) {
        setSuccess(true);
        // Track contact submission
        trackContactRequest(formData.service_type, formData.is_business);
        trackFormSubmission('contact_devis', {
          service_type: formData.service_type,
          is_business: formData.is_business
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          service_type: '',
          message: '',
          is_business: false,
          company_name: ''
        });
        setTimeout(() => setSuccess(false), 5000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Erreur lors de l\'envoi. Veuillez réessayer ou nous appeler.');
      }
    } catch (err) {
      setError('Erreur de connexion. Veuillez nous appeler au 02 78 78 04 04.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (cmsLoading) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C9A24D] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050509]">
      <Header activePage="Contact" />
      
      <main>
        {/* HEADER AVEC IMAGE */}
        <section className="relative h-[450px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/c0980e9b-610d-4a26-b736-a4ff38ba45a9/images/05b559b5e9c0f203ed75479be2324bd0bafb3a1055f602a06140f8389e8c9039.png"
              alt="Devis et contact Y GO"
              className="w-full h-full object-cover"
            />
            <div
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(90deg, rgba(11,11,11,0.9) 0%, rgba(11,11,11,0.7) 40%, rgba(11,11,11,0) 80%)'
              }}
            />
          </div>

          <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="max-w-3xl space-y-4">
              <p className="text-[10px] font-medium tracking-[0.35em] uppercase text-[#C9A24D]">
                Demande de devis & contact
              </p>
              <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold text-[#E5E5E5] leading-tight" style={{ textShadow: '0px 2px 10px rgba(0,0,0,0.5)' }}>
                Devis & <span className="text-[#C9A24D]">Contact</span>
              </h1>
              <p className="text-[#E5E5E5]/80 text-lg leading-relaxed max-w-2xl">
                Une demande, une réponse claire et personnalisée
              </p>
            </div>
          </div>
        </section>

        {/* CONTENU PRINCIPAL : FORMULAIRE + COORDONNÉES */}
        <section className="py-10 sm:py-16 lg:py-20 bg-[#050509]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
            <div className="grid lg:grid-cols-3 gap-6 sm:gap-8">
              
              {/* FORMULAIRE - 2/3 gauche */}
              <div className="lg:col-span-2">
                {/* Orientation de la demande */}
                <div className="mb-8">
                  <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#F5F5F5] mb-4">
                    Précisez votre <span className="text-[#C9A24D]">besoin</span>
                  </h2>
                  <p className="text-[#A7A7A7] text-sm mb-6">
                    Sélectionnez le type de service pour une réponse adaptée et rapide
                  </p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
                    {[
                      { value: 'transfert', label: 'Transfert', icon: Plane, desc: 'Aéroport, gare' },
                      { value: 'mise_a_dispo', label: 'Mise à disposition', icon: Clock, desc: 'Journée, demi-journée' },
                      { value: 'excursion', label: 'Excursion', icon: Ship, desc: 'Croisiériste, tourisme' },
                      { value: 'business', label: 'Business', icon: Building2, desc: 'Compte entreprise' },
                      { value: 'evenement', label: 'Événement', icon: Users, desc: 'Mariage, cérémonie' },
                      { value: 'autre', label: 'Autre', icon: HelpCircle, desc: 'Demande spécifique' }
                    ].map((type) => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => handleChange('service_type', type.value)}
                        className={`relative overflow-hidden p-4 text-center transition-all backdrop-blur-sm ${
                          formData.service_type === type.value
                            ? 'bg-[linear-gradient(135deg,rgba(201,162,77,0.25),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.85)] border-2 border-[#C9A24D]'
                            : 'bg-[linear-gradient(135deg,rgba(201,162,77,0.12),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] border border-[#C9A24D]/20 hover:border-[#C9A24D]/50'
                        }`}
                      >
                        <type.icon className={`w-6 h-6 mx-auto mb-2 ${formData.service_type === type.value ? 'text-[#C9A24D]' : 'text-[#C9A24D]/60'}`} />
                        <p className={`text-sm font-semibold mb-0.5 ${formData.service_type === type.value ? 'text-[#F5F5F5]' : 'text-[#E5E5E5]/80'}`}>
                          {type.label}
                        </p>
                        <p className="text-[#A7A7A7] text-xs">{type.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Formulaire principal */}
                <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-5 sm:p-8 backdrop-blur-sm">
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F5] mb-6">
                    Votre demande de devis
                  </h3>

                  {success && (
                    <div className="mb-6 bg-green-500/20 border border-green-500/50 p-4 rounded flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-green-200 font-semibold text-sm">Demande envoyée avec succès !</p>
                        <p className="text-green-200/80 text-xs mt-1">Nous vous répondrons sous 2h ouvrées.</p>
                      </div>
                    </div>
                  )}

                  {error && (
                    <div className="mb-6 bg-red-500/20 border border-red-500/50 p-4 rounded">
                      <p className="text-red-200 text-sm">{error}</p>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Nom & Email */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-[#E5E5E5]/80 text-sm mb-1.5 block">Nom complet *</label>
                        <Input
                          required
                          value={formData.name}
                          onChange={(e) => handleChange('name', e.target.value)}
                          className="bg-[#1F1F1F] border-[#C9A24D]/20 text-[#E5E5E5]"
                          placeholder="Jean Dupont"
                        />
                      </div>
                      <div>
                        <label className="text-[#E5E5E5]/80 text-sm mb-1.5 block">Email *</label>
                        <Input
                          required
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleChange('email', e.target.value)}
                          className="bg-[#1F1F1F] border-[#C9A24D]/20 text-[#E5E5E5]"
                          placeholder="email@exemple.com"
                        />
                      </div>
                    </div>

                    {/* Téléphone */}
                    <div>
                      <label className="text-[#E5E5E5]/80 text-sm mb-1.5 block">Téléphone *</label>
                      <Input
                        required
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        className="bg-[#1F1F1F] border-[#C9A24D]/20 text-[#E5E5E5]"
                        placeholder="+33 6 12 34 56 78"
                      />
                    </div>

                    {/* Case Business */}
                    <div className="space-y-3">
                      <label className="flex items-center gap-3 cursor-pointer group">
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={formData.is_business}
                            onChange={(e) => handleChange('is_business', e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-5 h-5 border-2 border-[#C9A24D]/40 bg-[#1F1F1F] peer-checked:bg-[#C9A24D] peer-checked:border-[#C9A24D] transition-all flex items-center justify-center">
                            {formData.is_business && (
                              <svg className="w-3 h-3 text-[#0B0B0B]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        </div>
                        <span className="text-[#E5E5E5]/80 text-sm group-hover:text-[#E5E5E5] transition-colors">
                          Demande professionnelle / Entreprise
                        </span>
                      </label>

                      {/* Nom de l'entreprise (conditionnel) */}
                      {formData.is_business && (
                        <div className="pl-8 animate-in slide-in-from-top-2 duration-200">
                          <label className="text-[#E5E5E5]/80 text-sm mb-1.5 block">Nom de l'entreprise *</label>
                          <Input
                            required={formData.is_business}
                            value={formData.company_name}
                            onChange={(e) => handleChange('company_name', e.target.value)}
                            className="bg-[#1F1F1F] border-[#C9A24D]/20 text-[#E5E5E5]"
                            placeholder="Nom de votre société"
                          />
                        </div>
                      )}
                    </div>

                    {/* Message */}
                    <div>
                      <label className="text-[#E5E5E5]/80 text-sm mb-1.5 block">Votre message</label>
                      <Textarea
                        value={formData.message}
                        onChange={(e) => handleChange('message', e.target.value)}
                        className="bg-[#1F1F1F] border-[#C9A24D]/20 text-[#E5E5E5] resize-none"
                        rows={4}
                        placeholder="Précisez votre besoin : horaires, bagages, options spécifiques, demande entreprise..."
                      />
                    </div>

                    {/* Submit */}
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold rounded-none py-4 text-base"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Envoyer ma demande de devis
                        </>
                      )}
                    </Button>
                    <p className="text-[#A7A7A7] text-xs text-center mt-2">
                      Réponse sous 2h en journée, sous 12h en soirée/weekend
                    </p>
                  </form>
                </div>

                {/* Cas spécifiques Business */}
                {formData.service_type === 'business' && (
                  <div className="mt-6 relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-6 backdrop-blur-sm border-l-4 border-[#C9A24D]">
                    <div className="flex items-start gap-3">
                      <Building2 className="w-6 h-6 text-[#C9A24D] flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="text-[#F5F5F5] font-semibold mb-2">Demande entreprise</h4>
                        <p className="text-[#D4D4D4] text-sm leading-relaxed">
                          Pour les demandes entreprises, nous étudions votre besoin en détail : volume mensuel, récurrence, options spécifiques, facturation. Un devis personnalisé vous sera envoyé sous <strong className="text-[#C9A24D]">48h ouvrées</strong> avec proposition tarifaire adaptée.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* COORDONNÉES + CARTE - 1/3 droite */}
              <div className="lg:col-span-1 space-y-6">
                {/* Contact direct */}
                <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-6 backdrop-blur-sm">
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F5] mb-6">
                    Contact direct
                  </h3>
                  
                  <div className="space-y-5">
                    <a href="tel:+33278780404" className="flex items-start gap-3 group">
                      <div className="w-10 h-10 bg-[#C9A24D]/12 flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-[#C9A24D]" />
                      </div>
                      <div>
                        <p className="text-[#A7A7A7] text-xs mb-0.5">Téléphone</p>
                        <p className="text-[#F5F5F5] font-semibold group-hover:text-[#C9A24D] transition-colors">
                          +33 2 78 78 04 04
                        </p>
                        <p className="text-[#A7A7A7] text-xs mt-0.5">24/7</p>
                      </div>
                    </a>

                    <a href="mailto:booking@y-go.fr" className="flex items-start gap-3 group">
                      <div className="w-10 h-10 bg-[#C9A24D]/12 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-[#C9A24D]" />
                      </div>
                      <div>
                        <p className="text-[#A7A7A7] text-xs mb-0.5">Email</p>
                        <p className="text-[#F5F5F5] font-semibold text-sm group-hover:text-[#C9A24D] transition-colors">
                          booking@y-go.fr
                        </p>
                        <p className="text-[#A7A7A7] text-xs mt-0.5">Réponse sous 2h</p>
                      </div>
                    </a>

                    <a href="mailto:business@y-go.fr" className="flex items-start gap-3 group">
                      <div className="w-10 h-10 bg-[#C9A24D]/12 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-5 h-5 text-[#C9A24D]" />
                      </div>
                      <div>
                        <p className="text-[#A7A7A7] text-xs mb-0.5">Entreprises</p>
                        <p className="text-[#F5F5F5] font-semibold text-sm group-hover:text-[#C9A24D] transition-colors">
                          business@y-go.fr
                        </p>
                        <p className="text-[#A7A7A7] text-xs mt-0.5">Devis sous 48h</p>
                      </div>
                    </a>

                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-[#C9A24D]/12 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-[#C9A24D]" />
                      </div>
                      <div>
                        <p className="text-[#A7A7A7] text-xs mb-0.5">Siège social</p>
                        <p className="text-[#F5F5F5] text-sm">
                          51 Bis Quai de Southampton
                        </p>
                        <p className="text-[#F5F5F5] text-sm">
                          76600 Le Havre
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-[#C9A24D]/12 flex items-center justify-center flex-shrink-0">
                        <Clock className="w-5 h-5 text-[#C9A24D]" />
                      </div>
                      <div>
                        <p className="text-[#A7A7A7] text-xs mb-0.5">Disponibilité</p>
                        <p className="text-[#F5F5F5] text-sm font-semibold">
                          24h/24 - 7j/7
                        </p>
                        <p className="text-[#A7A7A7] text-xs mt-0.5">Service permanent</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Carte Google Maps */}
                <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] backdrop-blur-sm">
                  <div className="h-64">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2595.7887894932726!2d0.10731791571441466!3d49.48789987934968!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e02f2d2e8c6a37%3A0x8e7c8b0e9f0f0f0f!2s51%20Bis%20Quai%20de%20Southampton%2C%2076600%20Le%20Havre!5e0!3m2!1sfr!2sfr!4v1702000000000!5m2!1sfr!2sfr"
                      width="100%"
                      height="100%"
                      style={{ border: 0, filter: 'grayscale(80%) contrast(1.1)' }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Y GO - 51 Bis Quai de Southampton, Le Havre"
                    />
                  </div>
                  <div className="p-4 bg-[#0B0B0B]/70">
                    <p className="text-[#E5E5E5] text-sm font-medium text-center mb-1">
                      51 Bis Quai de Southampton
                    </p>
                    <p className="text-[#A7A7A7] text-xs text-center">
                      76600 Le Havre, Normandie
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* RÉASSURANCE */}
        <section className="py-16 bg-[#0B0B0B] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <div className="text-center mb-10">
              <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-[#F5F5F5] mb-3">
                Pourquoi nous <span className="text-[#C9A24D]">contacter</span> ?
              </h2>
            </div>

            <div className="grid md:grid-cols-4 gap-6">
              {[
                { 
                  icon: Zap,
                  title: 'Réactivité',
                  desc: 'Réponse sous 2h en journée, 12h max le weekend'
                },
                { 
                  icon: Shield,
                  title: 'Confidentialité',
                  desc: 'Vos données ne sont jamais partagées ni revendues'
                },
                { 
                  icon: CheckCircle2,
                  title: 'Sérieux',
                  desc: 'Devis détaillé, sans engagement, valable 30 jours'
                },
                { 
                  icon: Users,
                  title: 'Accompagnement',
                  desc: 'Conseils personnalisés selon votre besoin'
                }
              ].map((item) => (
                <div key={item.title} className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-5 text-center backdrop-blur-sm">
                  <item.icon className="w-7 h-7 text-[#C9A24D] mx-auto mb-3" />
                  <h3 className="text-[#F5F5F5] font-semibold text-sm mb-1">{item.title}</h3>
                  <p className="text-[#A7A7A7] text-xs leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CAS SPÉCIFIQUES */}
        <section className="py-16 bg-[#050509] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <div className="text-center mb-10">
              <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-[#F5F5F5] mb-3">
                Demandes <span className="text-[#C9A24D]">spécifiques</span> & sur-mesure
              </h2>
              <p className="text-[#A7A7A7] text-sm max-w-2xl mx-auto">
                Chaque demande est étudiée et traitée individuellement
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  title: 'Comptes entreprises',
                  desc: 'Volume important, facturation mensuelle, responsable de compte dédié',
                  detail: 'Nous étudions vos flux de déplacements pour proposer une tarification adaptée et un service sur mesure (dashboard, reporting, SLA).',
                  icon: Building2
                },
                {
                  title: 'Déplacements internationaux',
                  desc: 'Coordination via notre réseau de partenaires dans 45 pays',
                  detail: 'Un seul contact Y GO pour organiser les déplacements de vos collaborateurs partout dans le monde. Standards de qualité uniformes.',
                  icon: Globe
                },
                {
                  title: 'Événements & volumes',
                  desc: 'Mariages, séminaires, congrès nécessitant plusieurs véhicules',
                  detail: 'Coordination de flotte, planning multi-trajets, véhicules décorables. Devis détaillé après étude de votre événement.',
                  icon: Users
                },
                {
                  title: 'Besoins récurrents',
                  desc: 'Navettes quotidiennes, missions longues, contrats annuels',
                  detail: 'Tarification préférentielle, planification anticipée, continuité de service garantie. Adaptation au fil de vos besoins.',
                  icon: Calendar
                }
              ].map((cas) => (
                <div key={cas.title} className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-6 backdrop-blur-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-[#C9A24D]/12 flex items-center justify-center">
                      <cas.icon className="w-5 h-5 text-[#C9A24D]" />
                    </div>
                    <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#F5F5F5]">
                      {cas.title}
                    </h3>
                  </div>
                  <p className="text-[#A7A7A7] text-sm mb-3">{cas.desc}</p>
                  <p className="text-[#D4D4D4] text-xs leading-relaxed">{cas.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ALTERNATIVE FORMULAIRE */}
        <section className="py-16 bg-[#0B0B0B] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
            <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-[#F5F5F5] mb-4">
              Vous préférez nous <span className="text-[#C9A24D]">appeler</span> ?
            </h2>
            <p className="text-[#A7A7A7] text-sm mb-8 max-w-2xl mx-auto">
              Notre équipe est joignable 24/7 pour répondre à vos questions et établir un devis par téléphone
            </p>

            <div className="grid sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
              <a 
                href="tel:+33278780404"
                className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] hover:shadow-[0_22px_55px_rgba(0,0,0,0.85)] transition-all p-8 text-center backdrop-blur-sm group"
              >
                <Phone className="w-10 h-10 text-[#C9A24D] mx-auto mb-4" />
                <p className="text-[#F5F5F5] font-semibold text-lg mb-1 group-hover:text-[#C9A24D] transition-colors">
                  +33 2 78 78 04 04
                </p>
                <p className="text-[#A7A7A7] text-sm">Assistance immédiate</p>
              </a>
              <a 
                href="mailto:booking@y-go.fr"
                className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] hover:shadow-[0_22px_55px_rgba(0,0,0,0.85)] transition-all p-8 text-center backdrop-blur-sm group"
              >
                <Mail className="w-10 h-10 text-[#C9A24D] mx-auto mb-4" />
                <p className="text-[#F5F5F5] font-semibold text-lg mb-1 group-hover:text-[#C9A24D] transition-colors">
                  booking@y-go.fr
                </p>
                <p className="text-[#A7A7A7] text-sm">Email prioritaire</p>
              </a>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}
